import { useState, useCallback } from 'react';
import { invoiceService, handleApiError, handlePaymentError, InvoiceItem, InvoiceListResponse, PaymentResponse } from '../services';
import { DmsDocumentResponse, InvoiceListOptions } from '../services/invoiceService';

export interface UseInvoicesReturn {
  invoices: InvoiceItem[];
  isLoading: boolean;
  error: string | null;
  total: number;
  hasMore: boolean;
  page: number;
  isProcessingPayment: boolean;
  dmsDocuments: DmsDocumentResponse | null;
  isDmsLoading: boolean;
  loadInvoices: (skip?: number, limit?: number, statusFilter?: string, options?: InvoiceListOptions) => Promise<void>;
  loadMoreInvoices: () => Promise<void>;
  downloadInvoice: (invoiceId: string) => Promise<void>;
  processPayment: (invoiceId: string) => Promise<PaymentResponse | null>;
  markPaid: (invoiceId: string) => Promise<void>;
  downloadReceipt: (invoiceId: string) => Promise<void>;
  loadDmsDocuments: (invoiceId: string, refresh?: boolean) => Promise<void>;
  downloadDmsDocument: (invoiceId: string, docType: string, docIndex?: number) => Promise<void>;
  clearError: () => void;
}

export const useInvoices = (): UseInvoicesReturn => {
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [currentSkip, setCurrentSkip] = useState(0);
  const [currentLimit, setCurrentLimit] = useState(20);
  const [currentStatusFilter, setCurrentStatusFilter] = useState<string>('all');
  // Held so loadMoreInvoices pages through the *same* result set the user is
  // looking at rather than resetting to the default sort.
  const [currentOptions, setCurrentOptions] = useState<InvoiceListOptions>({});
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [dmsDocuments, setDmsDocuments] = useState<DmsDocumentResponse | null>(null);
  const [isDmsLoading, setIsDmsLoading] = useState(false);

  const loadInvoices = useCallback(async (
    skip = 0,
    limit = 20,
    statusFilter = 'all',
    options: InvoiceListOptions = {},
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const response: InvoiceListResponse = await invoiceService.listInvoices(skip, limit, statusFilter, options);
      
      if (skip === 0) {
        // New search/filter - replace invoices
        setInvoices(response.invoices);
      } else {
        // Load more - append invoices
        setInvoices(prev => [...prev, ...response.invoices]);
      }
      
      setTotal(response.total);
      setHasMore(response.has_more);
      setPage(response.page);
      setCurrentSkip(skip);
      setCurrentLimit(limit);
      setCurrentStatusFilter(statusFilter);
      setCurrentOptions(options);
      
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMoreInvoices = useCallback(async () => {
    if (!hasMore || isLoading) return;
    
    const nextSkip = currentSkip + currentLimit;
    await loadInvoices(nextSkip, currentLimit, currentStatusFilter, currentOptions);
  }, [hasMore, isLoading, currentSkip, currentLimit, currentStatusFilter, currentOptions, loadInvoices]);

  const downloadInvoice = useCallback(async (invoiceId: string) => {
    try {
      await invoiceService.downloadInvoice(invoiceId);
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      setError(errorMessage);
      throw error;
    }
  }, []);

  const processPayment = useCallback(async (invoiceId: string): Promise<PaymentResponse | null> => {
    setIsProcessingPayment(true);
    setError(null);
    
    try {
      const response = await invoiceService.processPayment(invoiceId);
      return response;
    } catch (error: any) {
      const errorMessage = handlePaymentError(error);
      setError(errorMessage);
      return null;
    } finally {
      setIsProcessingPayment(false);
    }
  }, []);

  const markPaid = useCallback(async (invoiceId: string) => {
    setError(null);
    
    try {
      await invoiceService.markPaid(invoiceId);
      
      // Update the invoice status in the local state
      setInvoices(prev => prev.map(invoice => 
        invoice.id === invoiceId 
          ? { ...invoice, status: 'PAID' as const }
          : invoice
      ));
      
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      setError(errorMessage);
      throw error;
    }
  }, []);

  const downloadReceipt = useCallback(async (invoiceId: string) => {
    try {
      await invoiceService.downloadReceipt(invoiceId);
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      setError(errorMessage);
      throw error;
    }
  }, []);

  const loadDmsDocuments = useCallback(async (invoiceId: string, refresh = false) => {
    setIsDmsLoading(true);
    try {
      const response = await invoiceService.getDmsDocuments(invoiceId, refresh);
      setDmsDocuments(response);
    } catch (error: any) {
      console.error('Failed to load DMS documents:', error);
      // Don't set main error — DMS failure is non-critical
    } finally {
      setIsDmsLoading(false);
    }
  }, []);

  const downloadDmsDocument = useCallback(async (invoiceId: string, docType: string, docIndex = 0) => {
    try {
      await invoiceService.downloadDmsDocument(invoiceId, docType, docIndex);
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      setError(errorMessage);
      throw error;
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    invoices,
    isLoading,
    error,
    total,
    hasMore,
    page,
    isProcessingPayment,
    dmsDocuments,
    isDmsLoading,
    loadInvoices,
    loadMoreInvoices,
    downloadInvoice,
    processPayment,
    markPaid,
    downloadReceipt,
    loadDmsDocuments,
    downloadDmsDocument,
    clearError,
  };
};